import type {
	BaseTransportOptions,
	Breadcrumb,
	Envelope,
	ErrorEvent,
	SeverityLevel,
	StackFrame,
} from "@sentry/types";

export const WARN = "WARN";
export const ERROR = "ERROR";

export type Level = typeof WARN | typeof ERROR;

export const ENVIRONMENT = {
	PROD: "prod",
	STAGE: "stage",
	TEST: "test",
	EU: "eu",
	DEV: "dev",
} as const;

export type Environment = typeof ENVIRONMENT[keyof typeof ENVIRONMENT];

export const HOST_FRAGMENT = {
	TEST: "test",
	STAGE: "stage",
	EU: "eu.wevideo",
	DEV: "dev.wevideo",
} as const;

export const TAG = {
	APP_NAME: "appName",
	CODE_SOURCE: "trackjs_code_source",
	PAGE_URL: "page_url",
	IS_EXTERNAL: "trackjs_is_external",
	IS_VENDOR: "trackjs_is_vendor",
	LEVEL: "trackjs_level",
	INSTANCE_NAME: "instanceName",
	SESSION_ID: "sessionId",
	TRACKER: "error_tracker",
} as const;

export const TRACKER_SENTRY = "sentry";

export const SENTRY_LEVEL: Record<Level, SeverityLevel> = {
	[WARN]: "warning",
	[ERROR]: "error",
};

export type StackFrameClassifier = (frame: StackFrame) => boolean;

export type ErrorTrackingUser = {
	id: string | number;
	instanceName?: string;
	sessionId?: string;
};

// The SDK only enables itself and builds the transport when a syntactically
// valid DSN is present, but the real DSN is applied by the backend when it
// forwards the envelope. This placeholder satisfies the SDK without exposing it.
export const DEFAULT_DSN = "https://placeholder@sentry-placeholder.invalid/0";

export const DEFAULT_ENDPOINT = "/api/5/error-tracking";

export type ErrorTrackingConfig = {
	appName: string;
	appVersion: string;
	dsn?: string;
	// A getter is resolved per-send, for endpoints not known at init time; a plain
	// string is used as-is.
	endpoint?: string | (() => string);
	environment?: string;
	release?: string;
	ignoreMessages?: (string | RegExp)[];
	// Messages dropped entirely (not just downgraded to WARN), in addition to the
	// built-in spam list. Symmetric with `spamStackFragments` but matched on the
	// error message instead of the stack.
	spamMessages?: (string | RegExp)[];
	spamStackFragments?: (string | RegExp)[];
	isInternalFrame?: StackFrameClassifier;
	isVendorFrame?: StackFrameClassifier;
	// Drop errors whose stack contains no internal frame outright, instead of the
	// default behaviour of downgrading them to WARN and still reporting them. Useful
	// for contexts (e.g. a browser extension) where third-party page code is pure noise.
	dropExternal?: boolean;
	beforeSend?: (event: ErrorEvent) => ErrorEvent | null;
	// Runs after the built-in breadcrumb generalization; return null to drop the
	// breadcrumb. Lets consumers filter (e.g. asset/data-URL breadcrumbs) or reroute.
	beforeBreadcrumb?: (breadcrumb: Breadcrumb) => Breadcrumb | null;
	// Wrap the transport in Sentry's offline transport so envelopes are persisted to
	// IndexedDB and retried when the context comes back online / is resumed. Required
	// for MV3 service workers, which can be suspended between an error and its send.
	offline?: boolean;
	// Passed straight to `Sentry.init`'s `transportOptions`; carries offline-transport
	// settings (dbName, storeName, maxQueueSize, flushAtStartup, shouldStore).
	transportOptions?: ErrorTransportOptions;
};

// Sentry doesn't export its browser/offline transport option types from a stable
// entrypoint, so model the subset we pass to `Sentry.init`'s `transportOptions` on the
// public `BaseTransportOptions`. The offline-* fields are read by the offline transport.
export type ErrorTransportOptions = BaseTransportOptions & {
	dbName?: string;
	storeName?: string;
	maxQueueSize?: number;
	flushAtStartup?: boolean;
	shouldStore?: (envelope: Envelope, error: Error, retryDelay: number) => boolean;
};
